import { useState, useEffect, useRef, useMemo } from "react";
import BotonInstructivo from "./BotonInstructivo";
import { subirDatos, eliminarDato, sincronizarTodo } from "./syncSupabase";
import { logAction } from "./ALQOSLogger";

const STORAGE_MAQUINAS = "alqos_maquinas";
const STORAGE_CONFORMES = "alqos_conformes";
const STORAGE_REPORTES = "alqos_reportes_maquina";
const STORAGE_TPM = "alqos_tpm";
const BUCKET_REPORTES = "reportes-maquina";
const TIPOS_FOTO_REPORTE_PERMITIDOS = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

// Clave única para un paso puntual: OT + máquina + pliego + índice del paso. No usamos el
// nombre del paso como parte de la clave porque la secuencia se puede editar en Producción
// (agregar/sacar pasos) y el índice es lo único estable dentro de una misma carga.
function claveConforme(otNumero, idMaquina, pliegoNombre, pasoIndex) {
  return `${otNumero}__${idMaquina}__${pliegoNombre}__${pasoIndex}`;
}

// Catálogo real de la pyme, relevado a mano — 23 máquinas. El botón "Cargar catálogo
// inicial" solo agrega las que falten (match por nombre), nunca pisa ni duplica lo que
// ya esté cargado en Supabase.
const CATALOGO_INICIAL = [
  { nombre: "Troqueladora 1", modelo: "MK1", tipoProceso: "Troquelado" },
  { nombre: "Troqueladora 2", modelo: "MK2", tipoProceso: "Troquelado" },
  { nombre: "Descartonadora Automática", modelo: "SXL", tipoProceso: "Descartonado" },
  { nombre: "Cajonera 1", modelo: "", tipoProceso: "Cajonera" },
  { nombre: "Cajonera 2", modelo: "", tipoProceso: "Cajonera" },
  { nombre: "Acanaladora", modelo: "SAILIN", tipoProceso: "Acanalado" },
  { nombre: "Gofradora", modelo: "", tipoProceso: "Gofrado" },
  { nombre: "Troqueladora Manual", modelo: "GUANYIA", tipoProceso: "Troquelado" },
  { nombre: "Montadora", modelo: "AURIGIN", tipoProceso: "Montaje" },
  { nombre: "Guillotina 1", modelo: "", tipoProceso: "Corte" },
  { nombre: "Guillotina 2", modelo: "", tipoProceso: "Corte" },
  { nombre: "Laminadora", modelo: "GM3", tipoProceso: "Laminado" },
  { nombre: "Entapadora", modelo: "PP", tipoProceso: "Entapado" },
  { nombre: "Aplicadora de Imanes", modelo: "FKT 1000M", tipoProceso: "Aplicación de imanes" },
  { nombre: "Serigráfica", modelo: "JINBAO", tipoProceso: "Serigrafía" },
  { nombre: "Troqueladora Manual", modelo: "YUYIN 1040 1", tipoProceso: "Troquelado" },
  { nombre: "Troqueladora Manual", modelo: "YUYIN 1040 2", tipoProceso: "Troquelado" },
  { nombre: "Troqueladora Manual", modelo: "YAWA TYML 750", tipoProceso: "Troquelado" },
  { nombre: "Entapadora (chicas)", modelo: "JINTAI", tipoProceso: "Entapado" },
  { nombre: "Armadora de Águilas", modelo: "SINWEI", tipoProceso: "Armado" },
  { nombre: "Pegadora Pico y Base", modelo: "FKT SMART TECH", tipoProceso: "Pegado" },
  { nombre: "Pegadora", modelo: "Bost", tipoProceso: "Pegado" },
  { nombre: "Pegadora", modelo: "Easy", tipoProceso: "Pegado" },
];

const estadosMaquina = {
  operativa: { label: "✅ Operativa", color: "#22c55e" },
  mantenimiento: { label: "🛠️ En mantenimiento", color: "#f59e0b" },
  fuera_servicio: { label: "⛔ Fuera de servicio", color: "#e63946" },
};

// Fase 18 — reportes de planta sobre una máquina puntual, revisados por
// Calidad/Mantenimiento desde esta misma pantalla.
const estadosReporte = {
  pendiente: { label: "⏳ Pendiente de revisión", color: "#f59e0b" },
  incorporado_amfe: { label: "📋 Incorporado a AMFE", color: "#7B5BF2" },
  convertido_correctivo: { label: "🔧 Convertido a correctivo", color: "#e63946" },
  descartado: { label: "🚫 Descartado", color: "#6b7280" },
};

const storage = {
  get: (k, def = []) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(def)); } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { console.error(e); } },
};

function generarCod(lista) {
  return `MAQ-${String(lista.length + 1).padStart(3, "0")}`;
}

// Sube la foto opcional de un reporte a Storage con el tenant_id como
// primer segmento del path -- mismo patrón que subirUnCertificado()
// (IngresoMateriales.js) / subirLogoEmpresa() (AdminUsuarios.js): las
// policies RLS del bucket filtran exactamente por ese segmento.
async function subirFotoReporte(archivo, tenantId, idReporte, sufijo = "") {
  if (!tenantId) throw new Error("Sesión sin tenant_id -- no se puede subir el archivo.");
  const { supabase } = await import("./supabase");
  const ext = (archivo.name.split(".").pop() || "jpg").toLowerCase();
  const path = `${tenantId}/${idReporte}${sufijo}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET_REPORTES)
    .upload(path, archivo, { contentType: archivo.type, upsert: true });
  if (error) throw error;
  return { path, nombre: archivo.name, tipo: archivo.type };
}

// Un reporte puede tener más de un adjunto (foto + documento). `adjuntos`
// es el campo nuevo (array); `foto` queda como espejo del primer adjunto
// para compatibilidad con dispositivos que todavía tengan cargado el JS
// viejo durante el rollout, y con reportes creados antes de este cambio
// que solo tienen `foto` suelto.
function adjuntosDe(obj) {
  if (obj?.adjuntos?.length) return obj.adjuntos;
  if (obj?.foto) return [obj.foto];
  return [];
}

// Bridge reporte -> TPM correctivo. Mismo criterio que el bridge ya
// probado TPM -> NC (TPMMantenimiento.js:172-196): arma el objeto a mano
// con el shape del destino en vez de importar guardar()/generarIdTPM,
// que son closures privados atados al form del componente TPM.
function generarIdTPMDesdeReporte(listaTPM) {
  const anio = new Date().getFullYear();
  return `TPM-${anio}-${String(listaTPM.length + 1).padStart(4, "0")}`;
}

// Bridge reporte -> AMFE de máquina. Misma fórmula que generarIdFMEA() en
// FMEA.js (proceso/máquina como etiqueta, slice a 6 mayúsculas), duplicada
// a propósito acá por el mismo motivo de arriba -- generarIdFMEA es un
// closure de módulo privado en FMEA.js, no exportado.
function generarIdFMEAMaquina(maquinaNombre, listaFMEA) {
  const etiqueta = maquinaNombre?.replace(/\s+/g, "").slice(0, 6).toUpperCase() || "MAQ";
  const num = String(listaFMEA.length + 1).padStart(3, "0");
  return `FMEA-${etiqueta}-${num}`;
}

// Indicador de confiabilidad por máquina -- fórmulas de la cátedra de
// Confiabilidad (Maestría en Ingeniería en Calidad), modelo Exponencial
// (tasa de falla constante, válido en la zona plana de la curva de la
// bañera: ni mortalidad infantil ni desgaste). Ventana fija de 365 días
// terminando hoy; TT = 1 unidad (esta máquina) × horas de la ventana.
// NOT = suma de paradas reales de cada correctivo dentro de la ventana
// (fechaCompletado - fecha de falla; si sigue abierto, hasta ahora).
// Solo cuenta correctivos con maquinaId vinculado -- los cargados en
// texto libre antes de este indicador quedan afuera a propósito (ver
// convertirACorrectivoTPM más arriba y el selector nuevo en
// TPMMantenimiento.js).
const DIAS_VENTANA_CONFIABILIDAD = 365;

function calcularConfiabilidadMaquina(maquinaId, registrosTPM) {
  const ahora = Date.now();
  const inicioVentana = ahora - DIAS_VENTANA_CONFIABILIDAD * 24 * 3600 * 1000;
  const TT = DIAS_VENTANA_CONFIABILIDAD * 24;

  const correctivos = (registrosTPM || []).filter(r => {
    if (r.tipo !== "correctivo" || r.maquinaId !== maquinaId) return false;
    const fechaFalla = new Date(r.fechaCreacion || r.fecha).getTime();
    return fechaFalla >= inicioVentana && fechaFalla <= ahora;
  });

  const F = correctivos.length;
  const NOT = correctivos.reduce((acc, r) => {
    const inicio = new Date(r.fechaCreacion || r.fecha).getTime();
    const fin = r.fechaCompletado ? new Date(r.fechaCompletado).getTime() : ahora;
    return acc + Math.max(0, (fin - inicio) / 3600000);
  }, 0);

  const horasOperativas = Math.max(0, TT - NOT);
  const tasaFallas = F > 0 ? F / horasOperativas : 0;
  const mtbfHoras = F > 0 ? horasOperativas / F : null;
  const mtbfAnios = mtbfHoras != null ? mtbfHoras / (24 * 365) : null;

  return { F, TT, NOT, horasOperativas, tasaFallas, mtbfHoras, mtbfAnios, muestraInsuficiente: F < 5 };
}

export default function Maquinas({ onVolver, oscuro, usuario }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ estado: "operativa" });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  // OTs reales de ALQOS (las mismas de OrdenTrabajo.js / Producción) — se usan para validar el
  // número de OT que el operario tipea desde SISPRO y para mostrar los pliegos técnicos.
  const [ots] = useState(() => { try { return JSON.parse(localStorage.getItem('alqos_ots') || '[]'); } catch { return []; } });
  const [numeroOtBuscada, setNumeroOtBuscada] = useState("");
  const [otEncontrada, setOtEncontrada] = useState(null);
  const [errorBusquedaOt, setErrorBusquedaOt] = useState("");
  // Registro de "conforme" por paso: checklist (hecho) + datos numéricos (cantidad, tiempo,
  // scrap). Un registro por combinación OT+máquina+pliego+paso — se actualiza in-place en vez
  // de duplicar cada vez que se tilda o se edita un número.
  const [conformes, setConformes] = useState(() => storage.get(STORAGE_CONFORMES));
  // Fase 18 — reportes de planta. No tiene fetch propio a Supabase: baja con
  // sincronizarTodo() (syncSupabase.js) al loguearse, en el evento `online`,
  // o con el botón manual de acá abajo -- el listener a 'supabase-sync-complete'
  // más adelante en el componente es lo que refresca este estado sin recargar.
  const [reportes, setReportes] = useState(() => storage.get(STORAGE_REPORTES));
  // Indicador de confiabilidad -- lee el mismo caché que ya usa
  // convertirACorrectivoTPM() más abajo, pero como estado reactivo para
  // que el card se actualice solo con el mismo evento de sync que reportes.
  const [tpmRegistros, setTpmRegistros] = useState(() => storage.get(STORAGE_TPM));
  const [formReporteAbierto, setFormReporteAbierto] = useState(false);
  const [formReporte, setFormReporte] = useState({ descripcion: "", motivoSospechado: "" });
  // Dos slots separados -- antes "📷 Sacar foto" y "📎 Subir archivo" escribían
  // al mismo estado y el segundo pisaba al primero en silencio, así que nunca
  // se podían adjuntar los dos juntos (bug real: se perdía uno de los dos ya
  // en el formulario, antes de guardar el reporte).
  const [archivoFotoReporte, setArchivoFotoReporte] = useState(null);
  const [archivoDocReporte, setArchivoDocReporte] = useState(null);
  const [guardandoReporte, setGuardandoReporte] = useState(false);
  const [errorReporte, setErrorReporte] = useState("");
  const [filtroReportes, setFiltroReportes] = useState("pendiente");
  const [confirmDescartar, setConfirmDescartar] = useState(null);
  const [motivoDescarte, setMotivoDescarte] = useState("");
  const inputFotoReporteRef = useRef(null);
  const inputArchivoReporteRef = useRef(null);
  const [sincronizandoReportes, setSincronizandoReportes] = useState(false);

  // Fase 18 hallazgo 3: reportes creados en otro dispositivo (ej. el celular)
  // ya bajan bien con sincronizarTodo() (syncSupabase.js), pero esta pantalla
  // los tenía cacheados en el estado `reportes` desde el mount y no se
  // refrescaba sola -- mismo patrón que ya usan BalanceSimplificado.js/
  // MotorCosteo.js/CentrosCosto.js para este mismo evento.
  useEffect(() => {
    const onSync = () => {
      setReportes(storage.get(STORAGE_REPORTES));
      setTpmRegistros(storage.get(STORAGE_TPM));
    };
    window.addEventListener('supabase-sync-complete', onSync);
    return () => window.removeEventListener('supabase-sync-complete', onSync);
  }, []);

  // Botón manual para el caso de sesión restaurada (reload con sesión ya
  // activa): ese flujo no pasa por handleLogin() y por eso no dispara
  // sincronizarTodo() -- sin este botón, un reporte cargado desde el celular
  // no aparece acá hasta el próximo login real o un toggle real de conexión.
  const sincronizarManual = async () => {
    setSincronizandoReportes(true);
    try {
      await sincronizarTodo();
    } catch (e) {
      console.error("Error sincronizando reportes de planta:", e);
      alert("No se pudo sincronizar. Probá de nuevo.");
    } finally {
      setSincronizandoReportes(false);
    }
  };

  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import("./supabase");
        const { data } = await supabase.from("maquinas").select("data");
        if (data?.length) {
          const remotas = data.map(r => r.data);
          setItems(remotas);
          storage.set(STORAGE_MAQUINAS, remotas);
        } else {
          setItems(storage.get(STORAGE_MAQUINAS));
        }
      } catch {
        setItems(storage.get(STORAGE_MAQUINAS));
      }
      setCargando(false);
    }
    cargar();
  }, []);

  const confiabilidad = useMemo(
    () => detalle ? calcularConfiabilidadMaquina(detalle.id, tpmRegistros) : null,
    [detalle, tpmRegistros]
  );

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  // Fase 18: quién puede revisar reportes de planta (incorporar/convertir/descartar).
  const puedeRevisarReportes = usuario?.rol === "Administrador" || usuario?.rol === "Calidad";

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  if (cargando) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
      <p style={{ color: oscuro ? "white" : "#09184D", fontSize: "16px" }}>Cargando máquinas...</p>
    </div>
  );

  const guardar = () => {
    const nuevo = { ...form, id: Date.now(), codigo: generarCod(items), fechaCreacion: new Date().toISOString(), creadoPor: usuario?.nombre || "Sistema" };
    const nuevos = [...items, nuevo];
    setItems(nuevos);
    storage.set(STORAGE_MAQUINAS, nuevos);
    subirDatos(STORAGE_MAQUINAS);
    setForm({ estado: "operativa" });
    setVista("listado");
  };

  const actualizar = (id, campos) => {
    const nuevos = items.map(i => i.id === id ? { ...i, ...campos } : i);
    setItems(nuevos);
    storage.set(STORAGE_MAQUINAS, nuevos);
    subirDatos(STORAGE_MAQUINAS);
    if (detalle?.id === id) setDetalle(prev => ({ ...prev, ...campos }));
  };

  const eliminar = async (id) => {
    const nuevos = items.filter(i => i.id !== id);
    setItems(nuevos);
    storage.set(STORAGE_MAQUINAS, nuevos);
    await eliminarDato(STORAGE_MAQUINAS, id);
    setConfirmEliminar(null); setDetalle(null); setVista("listado");
  };

  // Valida el N° de OT que el operario tipea (el que ve en SISPRO) contra las OTs reales de
  // ALQOS. Match flexible (sin espacios, sin mayúsculas) porque a mano se tipean fácil con
  // espacios de más o mayúsculas distintas. Si no existe, NO deja avanzar — se muestra el
  // error y otEncontrada queda en null, así ninguna pantalla downstream puede asumir que hay
  // una OT válida cargada.
  const buscarOT = () => {
    const buscado = numeroOtBuscada.trim().toLowerCase();
    if (!buscado) { setErrorBusquedaOt("Ingresá un número de OT."); setOtEncontrada(null); return; }
    const encontrada = ots.find(ot => {
      const numero = (ot.op || ot.numero || "").toString().trim().toLowerCase();
      return numero === buscado;
    });
    if (!encontrada) {
      setOtEncontrada(null);
      setErrorBusquedaOt(`⛔ OT "${numeroOtBuscada}" no encontrada en ALQOS. Verificá el número contra SISPRO antes de continuar — no se puede avanzar con una OT que no existe en el sistema.`);
      return;
    }
    setErrorBusquedaOt("");
    setOtEncontrada(encontrada);
  };

  const limpiarBusquedaOt = () => {
    setNumeroOtBuscada("");
    setOtEncontrada(null);
    setErrorBusquedaOt("");
  };

  const obtenerConforme = (otNumero, idMaquina, pliegoNombre, pasoIndex) =>
    conformes.find(c => c.clave === claveConforme(otNumero, idMaquina, pliegoNombre, pasoIndex));

  // Crea o actualiza (nunca duplica) el registro de conforme de un paso puntual. Se llama
  // tanto al tildar el checkbox "hecho" como al completar cualquiera de los campos numéricos —
  // cada llamado guarda automático, sin botón "guardar" aparte, igual que el resto de ALQOS.
  const guardarConforme = (pliegoNombre, pasoIndex, pasoNombre, campos) => {
    if (!otEncontrada) return;
    const otNumero = otEncontrada.op || otEncontrada.numero;
    const clave = claveConforme(otNumero, detalle.id, pliegoNombre, pasoIndex);
    const existente = conformes.find(c => c.clave === clave);
    const base = existente || {
      id: Date.now() * 1000 + pasoIndex,
      clave,
      otNumero,
      otCliente: otEncontrada.cliente || "",
      otTrabajo: otEncontrada.trabajo || "",
      idMaquina: detalle.id,
      maquinaNombre: detalle.nombre + (detalle.modelo ? ` — ${detalle.modelo}` : ""),
      pliegoNombre,
      pasoIndex,
      pasoNombre,
      hecho: false,
      cantidad: "",
      tiempoMinutos: "",
      scrap: "",
    };
    const actualizado = { ...base, ...campos, ultimoCambio: new Date().toISOString(), registradoPor: usuario?.nombre || "Sistema" };
    const nuevos = existente ? conformes.map(c => c.clave === clave ? actualizado : c) : [...conformes, actualizado];
    setConformes(nuevos);
    storage.set(STORAGE_CONFORMES, nuevos);
    subirDatos(STORAGE_CONFORMES);
  };

  // ─── Fase 18: reportes de planta ────────────────────────────
  const guardarReporte = async () => {
    if (!formReporte.descripcion?.trim()) { setErrorReporte("Contá qué notaste raro o qué puede fallar."); return; }
    setErrorReporte("");
    setGuardandoReporte(true);
    try {
      const id = Date.now();
      const tenantId = Number(localStorage.getItem("alqos_tenant_id")) || null;
      const adjuntos = [];
      if (archivoFotoReporte) adjuntos.push(await subirFotoReporte(archivoFotoReporte, tenantId, id, ""));
      if (archivoDocReporte) adjuntos.push(await subirFotoReporte(archivoDocReporte, tenantId, id, "-doc"));
      const nuevo = {
        id,
        maquinaId: detalle.id,
        maquinaNombre: detalle.nombre + (detalle.modelo ? ` — ${detalle.modelo}` : ""),
        descripcion: formReporte.descripcion.trim(),
        motivoSospechado: formReporte.motivoSospechado?.trim() || "",
        adjuntos,
        foto: adjuntos[0] || null,
        estado: "pendiente",
        reportadoPor: usuario?.nombre || "Sistema",
        rolReportante: usuario?.rol || "",
        fechaCreacion: new Date().toISOString(),
      };
      const nuevos = [...reportes, nuevo];
      setReportes(nuevos);
      storage.set(STORAGE_REPORTES, nuevos);
      subirDatos(STORAGE_REPORTES);
      setFormReporte({ descripcion: "", motivoSospechado: "" });
      setArchivoFotoReporte(null);
      setArchivoDocReporte(null);
      setFormReporteAbierto(false);
    } catch (e) {
      setErrorReporte("Error al guardar el reporte: " + e.message);
    } finally {
      setGuardandoReporte(false);
    }
  };

  const actualizarReporte = (id, campos) => {
    const nuevos = reportes.map(r => r.id === id ? { ...r, ...campos } : r);
    setReportes(nuevos);
    storage.set(STORAGE_REPORTES, nuevos);
    subirDatos(STORAGE_REPORTES);
  };

  const descartarReporte = (id) => {
    if (!motivoDescarte.trim()) return;
    actualizarReporte(id, {
      estado: "descartado",
      motivoDescarte: motivoDescarte.trim(),
      revisadoPor: usuario?.nombre || "Sistema",
      fechaRevision: new Date().toISOString(),
    });
    setConfirmDescartar(null);
    setMotivoDescarte("");
  };

  // Bridge reporte -> TPM correctivo, mismo patrón que TPM -> NC
  // (TPMMantenimiento.js:172-196): arma el objeto a mano con el shape
  // tpm_v1 y lo escribe con subirDatos() (nunca upsert crudo), para que
  // tenant_id quede inyectado automáticamente -- mismo criterio de Fase 15.
  const convertirACorrectivoTPM = (reporte) => {
    const actualesTPM = storage.get("alqos_tpm");
    const adjuntos = adjuntosDe(reporte);
    const nuevo = {
      id: Date.now(),
      idTPM: generarIdTPMDesdeReporte(actualesTPM),
      tipo: "correctivo",
      pilar: "planificado",
      estado: "pendiente",
      maquina: reporte.maquinaNombre,
      maquinaId: reporte.maquinaId,
      sector: detalle.ubicacion || "",
      descripcion: `Reporte de planta: ${reporte.descripcion}${reporte.motivoSospechado ? ` — Posible causa: ${reporte.motivoSospechado}` : ""}`,
      responsable: usuario?.nombre || "Sistema",
      fecha: new Date().toISOString().slice(0, 10),
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema",
      origenReporte: reporte.id,
      adjuntos,
      foto: adjuntos[0] || null,
      _source: "localStorage", _schema: "tpm_v1",
    };
    storage.set("alqos_tpm", [...actualesTPM, nuevo]);
    subirDatos("alqos_tpm");
    logAction("TPM", "crear", { entidad: "OrdenTPM", idEntidad: nuevo.idTPM, despues: nuevo, detalle: "Generado desde reporte de planta" }, usuario);
    actualizarReporte(reporte.id, {
      estado: "convertido_correctivo",
      idTPMGenerado: nuevo.idTPM,
      revisadoPor: usuario?.nombre || "Sistema",
      fechaRevision: new Date().toISOString(),
    });
  };

  // Bridge reporte -> AMFE de máquina en FMEA.js. Cada fila de alqos_fmea
  // ya es un modo de falla individual (así funciona hoy el AMFE de
  // proceso) -- "crear AMFE nuevo" y "agregar modo de falla a uno
  // existente" son la MISMA operación acá: crear otra fila con el mismo
  // maquinaId. S-O-D quedan sin completar a propósito -- FMEA.js las
  // muestra como "sin evaluar" hasta que Calidad las complete ahí mismo.
  const incorporarAlAMFE = (reporte) => {
    const actualesFMEA = storage.get("alqos_fmea");
    const idFMEA = generarIdFMEAMaquina(reporte.maquinaNombre, actualesFMEA);
    const nuevo = {
      id: Date.now(),
      idFMEA,
      idRiesgo: `${idFMEA}-R01`,
      tipo: "maquina",
      maquinaId: reporte.maquinaId,
      maquinaNombre: reporte.maquinaNombre,
      modofalla: reporte.descripcion,
      causa: reporte.motivoSospechado || "",
      efecto: "",
      controlesActuales: "",
      severidad: "", ocurrencia: "", deteccion: "",
      npr: null, nivelNPR: null, critico: false, criticoAlto: false,
      responsable: usuario?.nombre || "Sistema",
      estado: "abierto",
      version: 1,
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema", rolCreador: usuario?.rol || "",
      origenReporte: reporte.id,
      _source: "localStorage", _schema: "fmea_v1",
    };
    storage.set("alqos_fmea", [...actualesFMEA, nuevo]);
    subirDatos("alqos_fmea");
    logAction("FMEA", "crear", { entidad: "FMEA", idEntidad: nuevo.idFMEA, despues: { idFMEA: nuevo.idFMEA, tipo: "maquina", maquinaNombre: nuevo.maquinaNombre }, detalle: "AMFE de máquina generado desde reporte de planta" }, usuario);
    actualizarReporte(reporte.id, {
      estado: "incorporado_amfe",
      idFMEAGenerado: nuevo.idFMEA,
      revisadoPor: usuario?.nombre || "Sistema",
      fechaRevision: new Date().toISOString(),
    });
  };

  const verFotoReporte = async (foto) => {
    if (!foto?.path) return;
    try {
      const { supabase } = await import("./supabase");
      const { data, error } = await supabase.storage.from(BUCKET_REPORTES).createSignedUrl(foto.path, 300);
      if (error || !data?.signedUrl) { alert("No se pudo abrir la foto."); return; }
      window.open(data.signedUrl, "_blank");
    } catch (e) { alert("No se pudo abrir la foto."); }
  };

  // Solo agrega las del catálogo real que todavía no estén cargadas (match por nombre+modelo) —
  // nunca pisa ni duplica lo que ya se haya editado a mano.
  const cargarCatalogoInicial = () => {
    const faltantes = CATALOGO_INICIAL.filter(c =>
      !items.some(i => i.nombre === c.nombre && i.modelo === c.modelo)
    );
    if (faltantes.length === 0) { alert("Ya están cargadas todas las máquinas del catálogo inicial."); return; }
    const nuevas = faltantes.map((c, idx) => ({
      ...c,
      id: Date.now() + idx,
      codigo: generarCod([...items, ...Array(idx).fill(null)]),
      estado: "operativa",
      deCatalogoInicial: true,
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema",
    }));
    const todas = [...items, ...nuevas];
    setItems(todas);
    storage.set(STORAGE_MAQUINAS, todas);
    subirDatos(STORAGE_MAQUINAS);
    alert(`✅ Se agregaron ${nuevas.length} máquina(s) del catálogo inicial.`);
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⚙️</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar esta máquina?</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo={detalle.codigo} onBack={() => { setDetalle(null); setVista("listado"); limpiarBusquedaOt(); }}
        extra={esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)} style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={card}>
          <span style={lbl}>Nombre</span>
          <input defaultValue={detalle.nombre || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { nombre: e.target.value })} style={inp} />
          <span style={lbl}>Modelo / marca</span>
          <input defaultValue={detalle.modelo || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { modelo: e.target.value })} style={inp} />
          <span style={lbl}>Tipo de proceso</span>
          <input defaultValue={detalle.tipoProceso || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { tipoProceso: e.target.value })} style={inp} />
          <span style={lbl}>Ubicación / sector</span>
          <input defaultValue={detalle.ubicacion || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { ubicacion: e.target.value })} style={inp} placeholder="opcional" />
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1C3FAA", marginBottom: "12px" }}>Estado</h3>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {Object.entries(estadosMaquina).map(([val, { label, color }]) => (
              <button key={val} disabled={!esAdmin} onClick={() => esAdmin && actualizar(detalle.id, { estado: val })}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: esAdmin ? "pointer" : "default", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>{label}</button>
            ))}
          </div>
        </div>

        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: formReporteAbierto ? "12px" : 0 }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#e63946", margin: 0 }}>⚠️ Reportar algo en esta máquina</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <button disabled={sincronizandoReportes} onClick={sincronizarManual} title="Traer reportes cargados desde otro dispositivo"
                style={{ padding: "6px 12px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: sincronizandoReportes ? "default" : "pointer", fontSize: "12px", color: texto, opacity: sincronizandoReportes ? 0.6 : 1 }}>
                {sincronizandoReportes ? "Sincronizando..." : "🔄 Sincronizar"}
              </button>
              {!formReporteAbierto && (
                <button onClick={() => setFormReporteAbierto(true)} style={{ padding: "6px 14px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>+ Reportar</button>
              )}
            </div>
          </div>
          {formReporteAbierto && (
            <div>
              <span style={lbl}>¿Qué puede fallar o qué notaste raro? *</span>
              <textarea rows={2} value={formReporte.descripcion} onChange={e => setFormReporte(f => ({ ...f, descripcion: e.target.value }))}
                style={{ ...inp, resize: "none" }} placeholder="Ej: la troqueladora hace un ruido raro al frenar" />
              <span style={lbl}>¿Por qué pensás que puede pasar? (opcional)</span>
              <textarea rows={2} value={formReporte.motivoSospechado} onChange={e => setFormReporte(f => ({ ...f, motivoSospechado: e.target.value }))}
                style={{ ...inp, resize: "none" }} placeholder="Ej: parece que es la correa" />
              <span style={lbl}>Foto y/o documento (opcional, se pueden cargar los dos)</span>
              <input type="file" ref={inputFotoReporteRef} accept="image/*" capture="environment" style={{ display: "none" }}
                onChange={e => setArchivoFotoReporte(e.target.files?.[0] || null)} />
              <input type="file" ref={inputArchivoReporteRef} accept={TIPOS_FOTO_REPORTE_PERMITIDOS.join(",")} style={{ display: "none" }}
                onChange={e => setArchivoDocReporte(e.target.files?.[0] || null)} />
              <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                <button onClick={() => inputFotoReporteRef.current?.click()} style={{ flex: 1, padding: "8px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: texto }}>📷 Sacar foto</button>
                <button onClick={() => inputArchivoReporteRef.current?.click()} style={{ flex: 1, padding: "8px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: texto }}>📎 Subir archivo</button>
              </div>
              {archivoFotoReporte && <p style={{ fontSize: "11px", color: subtexto, marginTop: "6px" }}>📷 {archivoFotoReporte.name}</p>}
              {archivoDocReporte && <p style={{ fontSize: "11px", color: subtexto, marginTop: "4px" }}>📎 {archivoDocReporte.name}</p>}
              {errorReporte && <p style={{ color: "#e63946", fontSize: "11px", margin: "6px 0 0" }}>{errorReporte}</p>}
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <button onClick={() => { setFormReporteAbierto(false); setFormReporte({ descripcion: "", motivoSospechado: "" }); setArchivoFotoReporte(null); setArchivoDocReporte(null); setErrorReporte(""); }}
                  style={{ padding: "10px 16px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "13px", color: texto }}>Cancelar</button>
                <button disabled={guardandoReporte} onClick={guardarReporte}
                  style={{ flex: 1, padding: "10px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: guardandoReporte ? "default" : "pointer", fontSize: "13px", fontWeight: "600", opacity: guardandoReporte ? 0.6 : 1 }}>
                  {guardandoReporte ? "Guardando..." : "Enviar reporte"}
                </button>
              </div>
            </div>
          )}
        </div>

        {confiabilidad && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1C3FAA", marginBottom: "4px" }}>📉 Confiabilidad (últimos 365 días)</h3>
            <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 10px" }}>
              Tasa de fallas y MTBF — modelo Exponencial (λ constante), solo correctivos vinculados a esta máquina desde que existe el selector en TPM.
            </p>
            {confiabilidad.muestraInsuficiente && (
              <div style={{ background: oscuro ? "#3a2a05" : "#fff8e1", border: "1px solid #f59e0b", borderRadius: "8px", padding: "8px 12px", marginBottom: "10px" }}>
                <p style={{ fontSize: "11px", color: "#f59e0b", fontWeight: "600", margin: 0 }}>⚠️ Muestra insuficiente ({confiabilidad.F} correctivo{confiabilidad.F === 1 ? "" : "s"} en la ventana) — el cálculo se muestra igual, pero no es estadísticamente confiable con menos de 5 fallas.</p>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "10px" }}>
              {[
                ["Correctivos (F)", confiabilidad.F, "#e63946"],
                ["Horas operativas", Math.round(confiabilidad.horasOperativas), "#3b82f6"],
                ["Tasa de fallas", confiabilidad.F > 0 ? confiabilidad.tasaFallas.toExponential(2) + " /hs" : "—", "#f59e0b"],
                ["MTBF (hs)", confiabilidad.mtbfHoras != null ? Math.round(confiabilidad.mtbfHoras) : "—", "#22c55e"],
                ["MTBF (años)", confiabilidad.mtbfAnios != null ? confiabilidad.mtbfAnios.toFixed(1) : "—", "#22c55e"],
              ].map(([lab, val, col]) => (
                <div key={lab} style={{ background: oscuro ? "#091840" : "#f8f9fa", borderRadius: "10px", padding: "10px", textAlign: "center", border: `1px solid ${borde}` }}>
                  <p style={{ fontSize: "16px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
                  <p style={{ fontSize: "10px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {reportes.some(r => r.maquinaId === detalle.id) && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1C3FAA", marginBottom: "10px" }}>📋 Reportes de planta</h3>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "12px" }}>
              {[["todos", "Todos"], ...Object.entries(estadosReporte).map(([val, { label }]) => [val, label])].map(([val, lab]) => (
                <button key={val} onClick={() => setFiltroReportes(val)}
                  style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroReportes === val ? "2px solid #1C3FAA" : `1px solid ${borde}`, background: filtroReportes === val ? "rgba(28,63,170,.1)" : cardBg, color: filtroReportes === val ? "#1C3FAA" : subtexto }}>{lab}</button>
              ))}
            </div>
            {reportes.filter(r => r.maquinaId === detalle.id && (filtroReportes === "todos" || r.estado === filtroReportes))
              .sort((a, b) => b.fechaCreacion.localeCompare(a.fechaCreacion))
              .map(r => (
                <div key={r.id} style={{ padding: "10px 0", borderBottom: `0.5px solid ${borde}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                    <p style={{ fontSize: "13px", color: texto, margin: 0, fontWeight: "500" }}>{r.descripcion}</p>
                    <span style={{ fontSize: "10px", fontWeight: "700", color: estadosReporte[r.estado]?.color, whiteSpace: "nowrap" }}>{estadosReporte[r.estado]?.label}</span>
                  </div>
                  {r.motivoSospechado && <p style={{ fontSize: "12px", color: subtexto, margin: "4px 0 0" }}>Posible causa: {r.motivoSospechado}</p>}
                  <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>
                    {r.reportadoPor} ({r.rolReportante}) · {r.fechaCreacion?.slice(0, 16).replace("T", " ")}
                    {adjuntosDe(r).map((a, i) => (
                      <span key={i}> · <button onClick={() => verFotoReporte(a)} style={{ background: "none", border: "none", color: "#3b82f6", cursor: "pointer", fontSize: "11px", padding: 0 }}>{a.tipo === "application/pdf" ? "📄" : "📷"} Ver {a.nombre || "adjunto"}</button></span>
                    ))}
                  </p>
                  {r.estado === "descartado" && r.motivoDescarte && <p style={{ fontSize: "11px", color: "#e63946", margin: "4px 0 0" }}>Motivo: {r.motivoDescarte}</p>}
                  {r.estado === "convertido_correctivo" && r.idTPMGenerado && <p style={{ fontSize: "11px", color: "#7B5BF2", margin: "4px 0 0" }}>→ {r.idTPMGenerado}</p>}
                  {r.estado === "incorporado_amfe" && r.idFMEAGenerado && <p style={{ fontSize: "11px", color: "#7B5BF2", margin: "4px 0 0" }}>→ {r.idFMEAGenerado}</p>}

                  {r.estado === "pendiente" && puedeRevisarReportes && (
                    confirmDescartar === r.id ? (
                      <div style={{ marginTop: "8px", padding: "8px", background: oscuro ? "#1a0505" : "#fff5f5", borderRadius: "8px" }}>
                        <input value={motivoDescarte} onChange={e => setMotivoDescarte(e.target.value)} placeholder="Motivo del descarte *"
                          style={{ ...inp, marginTop: 0, fontSize: "12px" }} />
                        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                          <button onClick={() => { setConfirmDescartar(null); setMotivoDescarte(""); }} style={{ padding: "6px 10px", background: "none", border: `1px solid ${borde}`, borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: texto }}>Cancelar</button>
                          <button disabled={!motivoDescarte.trim()} onClick={() => descartarReporte(r.id)} style={{ padding: "6px 10px", background: "#e63946", color: "white", border: "none", borderRadius: "6px", cursor: motivoDescarte.trim() ? "pointer" : "default", fontSize: "11px", fontWeight: "600", opacity: motivoDescarte.trim() ? 1 : 0.6 }}>Confirmar descarte</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                        <button onClick={() => incorporarAlAMFE(r)} style={{ padding: "6px 10px", background: "#7B5BF222", border: "1px solid #7B5BF2", borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: "#7B5BF2", fontWeight: "600" }}>📋 Incorporar al AMFE</button>
                        <button onClick={() => convertirACorrectivoTPM(r)} style={{ padding: "6px 10px", background: "#e6394622", border: "1px solid #e63946", borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: "#e63946", fontWeight: "600" }}>🔧 Convertir a correctivo TPM</button>
                        <button onClick={() => setConfirmDescartar(r.id)} style={{ padding: "6px 10px", background: "none", border: `1px solid ${borde}`, borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: subtexto, fontWeight: "600" }}>🚫 Descartar</button>
                      </div>
                    )
                  )}
                </div>
              ))
            }
          </div>
        )}

        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1C3FAA", marginBottom: "4px" }}>🔍 Buscar OT en esta máquina</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 10px" }}>Tipeá el número de OT que ves en SISPRO para traer la info técnica.</p>
          <div style={{ display: "flex", gap: "8px" }}>
            <input value={numeroOtBuscada} onChange={e => setNumeroOtBuscada(e.target.value)}
              onKeyDown={e => e.key === "Enter" && buscarOT()}
              placeholder="Ej: 9338" style={{ ...inp, marginTop: 0, flex: 1 }} />
            <button onClick={buscarOT} style={{ padding: "8px 16px", background: "#2A7FFF", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600", marginTop: 0 }}>Buscar</button>
            {(otEncontrada || errorBusquedaOt) && (
              <button onClick={limpiarBusquedaOt} style={{ padding: "8px 12px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", fontSize: "13px", color: subtexto, marginTop: 0 }}>✕</button>
            )}
          </div>

          {errorBusquedaOt && (
            <div style={{ marginTop: "12px", background: "#fff5f5", border: "1px solid #e63946", borderRadius: "8px", padding: "10px 14px" }}>
              <p style={{ fontSize: "12px", color: "#e63946", fontWeight: "600", margin: 0 }}>{errorBusquedaOt}</p>
            </div>
          )}

          {otEncontrada && (
            <div style={{ marginTop: "14px" }}>
              <div style={{ background: "#22c55e15", border: "1px solid #22c55e", borderRadius: "8px", padding: "10px 14px", marginBottom: "10px" }}>
                <p style={{ fontSize: "12px", color: "#22c55e", fontWeight: "700", margin: "0 0 4px" }}>✅ OT encontrada — {otEncontrada.op || otEncontrada.numero}</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{otEncontrada.cliente || "Sin cliente"} — {otEncontrada.trabajo || "Sin descripción"}</p>
              </div>
              {(otEncontrada.pliegosTecnicos || []).length === 0
                ? <p style={{ fontSize: "12px", color: subtexto }}>Esta OT todavía no tiene pliegos técnicos cargados desde Cotización.</p>
                : otEncontrada.pliegosTecnicos.map((grupo, gi) => (
                    <div key={gi} style={{ marginBottom: "10px" }}>
                      {grupo.itemDescripcion && <p style={{ fontSize: "12px", fontWeight: "700", color: subtexto, margin: "6px 0 4px" }}>{grupo.itemDescripcion}</p>}
                      {(grupo.pliegos || []).map((p, pi) => {
                        const secuencia = p.secuenciaProcesos || [];
                        const otNumero = otEncontrada.op || otEncontrada.numero;
                        const hechos = secuencia.filter((_, si) => obtenerConforme(otNumero, detalle.id, p.nombre, si)?.hecho).length;
                        return (
                          <div key={pi} style={{ background: oscuro ? "#0a1740" : "#F9FAFB", borderRadius: "8px", padding: "8px 12px", marginBottom: "6px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <p style={{ fontSize: "12px", fontWeight: "700", color: texto, margin: "0 0 2px" }}>{p.nombre}</p>
                              {secuencia.length > 0 && (
                                <span style={{ fontSize: "10px", fontWeight: "700", color: hechos === secuencia.length ? "#22c55e" : subtexto }}>
                                  {hechos === secuencia.length ? "✅ " : ""}{hechos}/{secuencia.length} pasos
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 6px" }}>{p.material || "sin material"} {p.valor ? `· ${p.valor}` : ""} {p.unidades ? `· ${p.unidades} un.` : ""}</p>
                            {secuencia.length === 0
                              ? <p style={{ fontSize: "11px", color: subtexto, margin: 0, fontStyle: "italic" }}>Sin secuencia de procesos cargada</p>
                              : secuencia.map((pasoNombre, si) => {
                                  const conforme = obtenerConforme(otNumero, detalle.id, p.nombre, si);
                                  return (
                                    <div key={si} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 0", borderBottom: si < secuencia.length - 1 ? `0.5px solid ${borde}` : "none", flexWrap: "wrap" }}>
                                      <input type="checkbox" checked={conforme?.hecho || false}
                                        onChange={e => guardarConforme(p.nombre, si, pasoNombre, { hecho: e.target.checked })}
                                        style={{ width: "14px", height: "14px", cursor: "pointer", flexShrink: 0 }} />
                                      <span style={{ fontSize: "11px", color: conforme?.hecho ? "#22c55e" : texto, fontWeight: conforme?.hecho ? "700" : "400", flex: "1 1 100px" }}>{pasoNombre}</span>
                                      <input type="number" placeholder="Cant." defaultValue={conforme?.cantidad ?? ""}
                                        onBlur={e => guardarConforme(p.nombre, si, pasoNombre, { cantidad: e.target.value })}
                                        style={{ width: "56px", padding: "3px 5px", fontSize: "10px", borderRadius: "5px", border: `1px solid ${borde}`, background: cardBg, color: texto }} />
                                      <input type="number" placeholder="Min" defaultValue={conforme?.tiempoMinutos ?? ""}
                                        onBlur={e => guardarConforme(p.nombre, si, pasoNombre, { tiempoMinutos: e.target.value })}
                                        style={{ width: "48px", padding: "3px 5px", fontSize: "10px", borderRadius: "5px", border: `1px solid ${borde}`, background: cardBg, color: texto }} />
                                      <input type="number" placeholder="Scrap" defaultValue={conforme?.scrap ?? ""}
                                        onBlur={e => guardarConforme(p.nombre, si, pasoNombre, { scrap: e.target.value })}
                                        style={{ width: "52px", padding: "3px 5px", fontSize: "10px", borderRadius: "5px", border: `1px solid ${borde}`, background: cardBg, color: texto }} />
                                    </div>
                                  );
                                })
                            }
                          </div>
                        );
                      })}
                    </div>
                  ))
              }
            </div>
          )}
        </div>

        <div style={card}>
          <span style={lbl}>Observaciones</span>
          <textarea rows={3} defaultValue={detalle.observaciones || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { observaciones: e.target.value })} style={{ ...inp, resize: "none" }} />
        </div>
      </div>
    </div>
  );

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva Máquina" onBack={() => setVista("listado")} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={card}>
          <span style={lbl}>Nombre</span><input onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} style={inp} placeholder="Ej: Troqueladora 3" />
          <span style={lbl}>Modelo / marca</span><input onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))} style={inp} placeholder="Ej: MK3" />
          <span style={lbl}>Tipo de proceso</span><input onChange={e => setForm(f => ({ ...f, tipoProceso: e.target.value }))} style={inp} placeholder="Ej: Troquelado" />
          <span style={lbl}>Ubicación / sector</span><input onChange={e => setForm(f => ({ ...f, ubicacion: e.target.value }))} style={inp} placeholder="opcional" />
          <span style={lbl}>Estado</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {Object.entries(estadosMaquina).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, estado: val }))}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: form.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.estado === val ? `${color}22` : cardBg, color: form.estado === val ? color : texto }}>{label}</button>
            ))}
          </div>
          <span style={lbl}>Observaciones</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} style={{ ...inp, resize: "none" }} />
        </div>
        <button onClick={guardar} disabled={!form.nombre} style={{ width: "100%", padding: "14px", background: form.nombre ? "#1C3FAA" : "#9aabd4", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: form.nombre ? "pointer" : "default" }}>Guardar Máquina</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="maquinas" oscuro={oscuro} />
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Máquinas</span>
        </div>
        {esAdmin && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={cargarCatalogoInicial} style={{ padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>📋 Cargar catálogo inicial</button>
            <button onClick={() => setVista("nueva")} style={{ padding: "6px 14px", background: "#2A7FFF", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva</button>
          </div>
        )}
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total", items.length, "#1C3FAA"], ["Operativas", items.filter(i => i.estado === "operativa").length, "#22c55e"], ["En mantenimiento", items.filter(i => i.estado === "mantenimiento").length, "#f59e0b"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {items.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>⚙️</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay máquinas registradas</p>
              {esAdmin && <p style={{ fontSize: "13px", color: subtexto, marginTop: "8px" }}>Usá "📋 Cargar catálogo inicial" para precargar las 23 máquinas reales de la pyme.</p>}
            </div>
          : items.map(item => (
              <div key={item.id} onClick={() => { setDetalle(item); setVista("detalle"); }}
                style={{ background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${borde}`, marginBottom: "10px", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(28,63,170,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <span style={{ fontWeight: "700", color: "#1C3FAA", fontSize: "13px" }}>{item.codigo}</span>
                    <span style={{ marginLeft: "10px", fontSize: "13px", color: texto, fontWeight: "600" }}>{item.nombre}{item.modelo ? ` — ${item.modelo}` : ""}</span>
                    <p style={{ fontSize: "12px", color: subtexto, marginTop: "4px" }}>{item.tipoProceso || "Sin tipo de proceso"} {item.ubicacion ? `· ${item.ubicacion}` : ""}</p>
                  </div>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: estadosMaquina[item.estado]?.color }}>{estadosMaquina[item.estado]?.label}</span>
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
